/**
 * AvatarOverlay — floating, draggable panel that hosts the BankerAvatar.
 *
 * Drag handle: the grip bar at the top of the expanded card / the pill itself.
 * Position is clamped to the viewport on every move and on window resize.
 *
 * Story v3-7-4: 2D Animated Banker Character
 * Story v4-9-1: Added `variant` prop for BankerAvatar3D.
 * Drag: pointer-event drag so the overlay can be repositioned freely.
 */

import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { X, Minus, GripHorizontal } from 'lucide-react';
import BankerAvatar from './BankerAvatar';
import type { AvatarState, AvatarPersona } from './BankerAvatar';

// Lazy-load the 3D renderer so Three.js / R3F bundle is NOT loaded unless needed (AC5)
const BankerAvatar3D = React.lazy(() => import('./BankerAvatar3D'));

/** Strip characters that could interfere with HTML attributes or screen readers. */
function sanitizeName(name: string): string {
  return name.replace(/[<>"'`\\]/g, '').slice(0, 100).trim();
}

interface AvatarOverlayProps {
  personaName: string;
  persona: AvatarPersona;
  avatarState: AvatarState;
  isVisible: boolean;
  onClose: () => void;
  /** '2d' renders the SVG avatar; '3d' renders the R3F canvas (default: '2d') */
  variant?: '2d' | '3d';
  /** Ref to the currently playing audio element — used by the 3D lip sync hook */
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  /**
   * When true, shows a dual-persona convergence badge on the overlay —
   * indicates that both Analyst and Banker signals are aligned (AC3).
   */
  isConvergence?: boolean;
}

/** Accent colours that match BankerAvatar */
const ACCENT_CLASS: Record<AvatarPersona, string> = {
  analyst: 'bg-blue-500',
  banker: 'bg-amber-500',
  copilot: 'bg-teal-500',
};

const CARD_W = 200;
const CARD_H = 300;
const PILL_SIZE = 48;

function clampPos(x: number, y: number, w: number, h: number) {
  return {
    x: Math.max(0, Math.min(x, window.innerWidth - w)),
    y: Math.max(0, Math.min(y, window.innerHeight - h)),
  };
}

const AvatarOverlay: React.FC<AvatarOverlayProps> = ({
  personaName,
  persona,
  avatarState,
  isVisible,
  onClose,
  variant = '2d',
  audioRef,
  isConvergence = false,
}) => {
  const safePersonaName = sanitizeName(personaName);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const prevVisible = useRef(false);

  // ── Drag state ────────────────────────────────────────────────────
  const defaultPos = useCallback(() => ({
    x: window.innerWidth - CARD_W - 24,
    y: window.innerHeight - CARD_H - 96,
  }), []);

  const [pos, setPos] = useState(defaultPos);
  const dragOrigin = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);
  const dragged = useRef(false);

  useEffect(() => {
    const onResize = () => {
      const s = isMinimized ? PILL_SIZE : CARD_W;
      const h = isMinimized ? PILL_SIZE : CARD_H;
      setPos(prev => clampPos(prev.x, prev.y, s, h));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMinimized]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragged.current = false;
    dragOrigin.current = { mx: e.clientX, my: e.clientY, ox: pos.x, oy: pos.y };
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragOrigin.current) return;
    const dx = e.clientX - dragOrigin.current.mx;
    const dy = e.clientY - dragOrigin.current.my;
    if (!dragged.current && Math.hypot(dx, dy) > 4) dragged.current = true;
    if (!dragged.current) return;
    const w = isMinimized ? PILL_SIZE : CARD_W;
    const h = isMinimized ? PILL_SIZE : CARD_H;
    setPos(clampPos(dragOrigin.current.ox + dx, dragOrigin.current.oy + dy, w, h));
  }, [isMinimized]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragOrigin.current = null;
    // Reset dragged flag after click handlers have a chance to read it
    setTimeout(() => { dragged.current = false; }, 0);
  }, []);

  // ── Slide-in trigger ──────────────────────────────────────────────
  useEffect(() => {
    if (isVisible && !prevVisible.current) {
      setIsMounted(false);
      setPos(defaultPos());
      const t = setTimeout(() => setIsMounted(true), 16);
      prevVisible.current = true;
      return () => clearTimeout(t);
    }
    if (!isVisible) {
      setIsMounted(false);
      prevVisible.current = false;
    }
  }, [isVisible, defaultPos]);

  if (!isVisible) return null;

  // ── Inner avatar renderer ─────────────────────────────────────────
  const avatarRenderer = variant === '3d' ? (
    <Suspense fallback={<BankerAvatar state={avatarState} persona={persona} size="md" />}>
      <BankerAvatar3D
        state={avatarState}
        persona={persona}
        size="md"
        audioRef={audioRef}
        modelOffset={[0, -1.4, 0]}
      />
    </Suspense>
  ) : (
    <BankerAvatar state={avatarState} persona={persona} size="md" />
  );

  // ── Minimised pill ───────────────────────────────────────────────
  if (isMinimized) {
    return (
      <div
        data-testid="avatar-overlay"
        style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 55, touchAction: 'none' }}
        aria-label={`${safePersonaName} avatar (minimised)`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <button
          onClick={() => { if (!dragged.current) setIsMinimized(false); }}
          className={`
            w-12 h-12 rounded-full
            ${ACCENT_CLASS[persona]}
            shadow-lg flex items-center justify-center overflow-hidden
            cursor-grab active:cursor-grabbing
            transition-transform duration-200 hover:scale-105
          `}
          title={`Expand ${safePersonaName}`}
          style={{
            transform: isMounted ? 'scale(1)' : 'scale(0)',
            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div style={{ transform: 'scale(0.7) translateY(8px)', marginTop: '-8px' }}>
            <BankerAvatar state={avatarState} persona={persona} size="sm" />
          </div>
        </button>
      </div>
    );
  }

  // ── Expanded card ────────────────────────────────────────────────
  return (
    <div
      data-testid="avatar-overlay"
      style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 55 }}
      aria-label={`${safePersonaName} avatar`}
    >
      <div
        className="
          w-[200px]
          bg-slate-800 border border-slate-700
          rounded-2xl shadow-xl shadow-black/40
          flex flex-col items-center overflow-hidden
        "
        style={{
          transform: isMounted ? 'translateY(0)' : 'translateY(40px)',
          opacity: isMounted ? 1 : 0,
          transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
        }}
      >
        {/* Drag handle / control bar */}
        <div
          className="w-full flex items-center justify-between px-2 pt-2 pb-1 cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          title="Drag to move"
        >
          <GripHorizontal size={14} className="text-slate-600 pointer-events-none" />
          <div className="flex items-center gap-1">
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={() => { if (!dragged.current) setIsMinimized(true); }}
              className="p-1 text-slate-400 hover:text-slate-200 transition-colors rounded"
              title="Minimise"
              aria-label="Minimise avatar"
            >
              <Minus size={14} />
            </button>
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={() => { if (!dragged.current) onClose(); }}
              className="p-1 text-slate-400 hover:text-slate-200 transition-colors rounded"
              title="Close"
              aria-label="Close avatar"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Avatar body */}
        <div className="flex flex-col items-center pb-4 px-3 gap-2 w-full">
          {isConvergence && (
            <div className="flex items-center gap-1 self-end -mt-1 -mr-1" title="Analyst + Banker convergence">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-1 ring-slate-800" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 ring-1 ring-slate-800" />
            </div>
          )}

          {avatarRenderer}

          <span className="text-sm font-medium text-slate-200 text-center leading-tight">
            {safePersonaName}
          </span>

          <StateBadge state={avatarState} accent={ACCENT_CLASS[persona]} />
        </div>
      </div>
    </div>
  );
};

// ── Small state indicator badge ────────────────────────────────────────────

const STATE_LABELS: Record<AvatarState, string> = {
  idle: 'Standby',
  thinking: 'Thinking…',
  speaking: 'Speaking',
  confident: 'Confident',
  concerned: 'Alert',
  greeting: 'Hello!',
};

const StateBadge: React.FC<{ state: AvatarState; accent: string }> = ({ state, accent }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs text-slate-300 bg-slate-700/70">
    <span className={`w-1.5 h-1.5 rounded-full ${accent} ${state === 'speaking' || state === 'thinking' ? 'animate-pulse' : ''}`} />
    {STATE_LABELS[state]}
  </span>
);

export default AvatarOverlay;
